import { CreditCard, User } from 'lucide-react';
import Layout from '../components/Layout';
import { SectionCard } from '../components/ui/SectionCard';
import { Skeleton } from '../components/ui/Skeleton';
import { useUserProfile } from '../hooks/useUserProfile';

export default function Profile() {
  const { profile, loading } = useUserProfile();

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile &amp; Settings</h2>

        {/* Account info */}
        <SectionCard title="Account" actions={<User className="h-4 w-4 text-gray-400" />}>
          <dl className="space-y-3">
            <div className="flex items-center justify-between">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="text-sm text-gray-900">
                {loading ? <Skeleton className="h-4 w-32" /> : (profile?.displayName ?? '—')}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="text-sm text-gray-900">
                {loading ? <Skeleton className="h-4 w-48" /> : (profile?.email ?? '—')}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm font-medium text-gray-500">Member since</dt>
              <dd className="text-sm text-gray-900">
                {loading ? (
                  <Skeleton className="h-4 w-28" />
                ) : profile?.createdAt ? (
                  profile.createdAt.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>
        </SectionCard>

        {/* Subscription */}
        <SectionCard
          title="Subscription"
          actions={<CreditCard className="h-4 w-4 text-gray-400" />}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              Free plan
            </span>
          </div>

          <p className="text-sm text-gray-500 mb-5">
            You're on the <span className="font-semibold text-gray-700">Free</span> plan.
            Upgrade to unlock unlimited subscribers, webhooks, Slack notifications, domain
            protection, CSV export, and more.
          </p>

          {/* Pricing tiers */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            {/* Free tier */}
            <div className="rounded-xl border-2 border-brand-500 p-4 flex flex-col gap-1">
              <p className="text-sm font-bold text-gray-900">Free</p>
              <p className="text-2xl font-extrabold text-gray-900">
                $0<span className="text-sm font-medium text-gray-500">/mo</span>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>✓ 1 project</li>
                <li>✓ Up to 100 subscribers</li>
                <li>✓ Basic analytics</li>
                <li>✓ Drop-in embed</li>
              </ul>
            </div>

            {/* Starter tier */}
            <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-1 bg-gray-50">
              <p className="text-sm font-bold text-gray-900">Starter</p>
              <p className="text-2xl font-extrabold text-gray-900">
                $5<span className="text-sm font-medium text-gray-500">/mo</span>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>✓ 1 project</li>
                <li>✓ Unlimited subscribers</li>
                <li>✓ Webhooks &amp; Slack</li>
                <li>✓ Domain protection</li>
                <li>✓ CSV export</li>
                <li>✓ API keys</li>
              </ul>
            </div>

            {/* Pro tier */}
            <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-1 bg-gray-50">
              <p className="text-sm font-bold text-gray-900">Pro</p>
              <p className="text-2xl font-extrabold text-gray-900">
                $15<span className="text-sm font-medium text-gray-500">/mo</span>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>✓ Unlimited projects</li>
                <li>✓ Unlimited subscribers</li>
                <li>✓ Everything in Starter</li>
                <li>✓ Priority support</li>
              </ul>
            </div>
          </div>

          <button
            disabled
            title="Billing coming soon"
            className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-lg opacity-50 cursor-not-allowed"
          >
            Upgrade — coming soon
          </button>
        </SectionCard>
      </div>
    </Layout>
  );
}
